import type { LeagueConfig } from '../types/league';
import type { LeagueFormatType } from '../components/LeagueFormatSelector';

export interface SharedLeagueConfigInput {
  format: LeagueFormatType;
  scoringFormat?: 'standard' | 'ppr' | 'half-ppr';
  teamCount?: number;
  isSuperflex?: boolean;
}

/**
 * Creates a standardized LeagueConfig object used by both Player Rankings and Trade Calculator
 * Ensures perfect alignment between both systems
 */
export function createSharedLeagueConfig(input: SharedLeagueConfigInput): LeagueConfig {
  const { 
    format, 
    scoringFormat = 'standard', 
    teamCount = 12,
    isSuperflex = false 
  } = input;

  // Determine superflex status based on format
  const actualSuperflex = format === 'superflex' || format === '2qb' || isSuperflex;

  return {
    id: `shared-league-${format}`,
    name: `Shared League (${format})`,
    format: {
      type: format === 'standard' ? 'standard' : format,
      teams: teamCount,
      rounds: 16,
      isPPR: scoringFormat === 'ppr' || scoringFormat === 'half-ppr',
      pprValue: scoringFormat === 'ppr' ? 1 : scoringFormat === 'half-ppr' ? 0.5 : 0
    },
    roster: {
      starters: {
        qb: 1,
        rb: 2,
        wr: 2,
        te: 1,
        flex: 1,
        superflex: actualSuperflex ? 1 : 0,
        k: format === 'dynasty' || format === 'bestball' ? 0 : 1,
        def: format === 'dynasty' || format === 'bestball' ? 0 : 1
      },
      bench: 6
    },
    draftSettings: {
      type: 'snake',
      timePerPick: 90,
      tradingEnabled: true
    },
    scoring: {
      passing: { 
        passingYards: 0.04, 
        passingTD: 4, 
        interception: -2, 
        passing2PC: 2 
      },
      rushing: { 
        rushingYards: 0.1, 
        rushingTD: 6, 
        rushing2PC: 2 
      },
      receiving: { 
        receivingYards: 0.1, 
        receivingTD: 6, 
        reception: scoringFormat === 'ppr' ? 1 : scoringFormat === 'half-ppr' ? 0.5 : 0,
        receiving2PC: 2
      },
      kicking: { 
        pat: 1, 
        fg0to19: 3, 
        fg20to29: 3, 
        fg30to39: 3, 
        fg40to49: 4, 
        fg50plus: 5, 
        fgMiss: -1 
      },
      defense: { 
        defensiveTD: 6, 
        interception: 2, 
        fumbleRecovery: 2, 
        sack: 1, 
        safety: 2, 
        pointsAllowed0: 10, 
        pointsAllowed1to6: 7, 
        pointsAllowed7to13: 4, 
        pointsAllowed14to20: 1, 
        pointsAllowed21to27: 0, 
        pointsAllowed28to34: -1, 
        pointsAllowed35plus: -4 
      }
    }
  };
}

/**
 * Legacy LeagueSettings converter for backward compatibility
 */
export interface LegacyLeagueSettings {
  leagueSize: number;
  startingSlots: {
    QB: number;
    RB: number;
    WR: number;
    TE: number;
    K?: number;
    DEF?: number;
    FLEX?: number;
    SUPERFLEX?: number;
  };
}

/**
 * Convert legacy LeagueSettings to modern LeagueConfig
 */
export function convertLegacyLeagueSettings(
  legacySettings: LegacyLeagueSettings,
  format: LeagueFormatType = 'standard',
  scoringFormat: 'standard' | 'ppr' | 'half-ppr' = 'standard'
): LeagueConfig {
  const hasSuperflex = (legacySettings.startingSlots.SUPERFLEX || 0) > 0 || 
                      legacySettings.startingSlots.QB > 1;

  return createSharedLeagueConfig({
    format: hasSuperflex ? 'superflex' : format,
    scoringFormat,
    teamCount: legacySettings.leagueSize,
    isSuperflex: hasSuperflex
  });
}